import React, { useState, useEffect } from 'react';
import BookGrid from '../home/exploreBooksComponents/BookGrid';
import AuthorGrid from '../home/exploreAuthorComponents/AuthorGrid';
import SearchService from '../../services/home/BookManager';
import { FiSearch, FiGrid, FiList } from 'react-icons/fi';

const SearchPage = () => {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('grid');
  const [books, setBooks] = useState([]);
  const [authors, setAuthors] = useState([]);

  // Add debounce for better UX
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (keyword.trim()) handleSearch();
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [keyword]);

  const handleSearch = async () => {
    if (!keyword.trim()) return;
    setLoading(true);
    try {
      const { books: fetchedBooks, authors } = await SearchService.searchAllEntities(keyword);
      
      const mappedBooks = fetchedBooks.map(b => ({
        id: b.bookId || b.id,
        title: b.bookName || b.title,
        author: b.author?.authorName || b.author,
        isbn: b.bookIsbn13 || b.isbn,
        description: b.bookDescription || " ",
        rating: Math.round(b.rating || 0),
        image: Array.isArray(b.bookImages) ? b.bookImages[0] : b.image,
      }));

      const mappedAuthors = authors.map(a => ({
        id: a.authorId ?? a.id,
        name: a.authorName ?? a.name,
        image: a.authorImage ?? a.image ?? '/images/default-author.png',
        aboutAuthor: a.aboutAuthor ?? '',
      }));

      setBooks(mappedBooks);
      setAuthors(mappedAuthors);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4 dark:bg-slate-900 min-h-screen">
      {/* Search Header */}
      <div className="max-w-3xl mx-auto mb-12 space-y-6">
        <h1 className="text-4xl font-bold text-center bg-gradient-to-r from-primary-shade4 to-primary-shade2 bg-clip-text text-transparent">
          Discover Your Next Read
        </h1>
        
        <div className="relative group">
          <input
            type="text"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            placeholder="Search books, authors, categories..."
            className="w-full px-6 py-4 rounded-xl border-0 shadow-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm focus:ring-2 focus:ring-primary-shade3 focus:shadow-2xl transition-all duration-300 pr-16"
            aria-label="Search input"
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-gradient-to-br from-primary-shade3 to-primary-shade4 rounded-lg hover:scale-105 transition-transform"
            aria-label="Search button"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
            ) : (
              <FiSearch className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex justify-end mb-8 space-x-2">
        <button
          onClick={() => setView('grid')}
          className={`p-2 rounded-lg ${view === 'grid' ? 'bg-primary-shade1 dark:bg-slate-700 text-primary-shade4' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}
        >
          <FiGrid className="w-6 h-6" />
        </button>
        <button
          onClick={() => setView('list')}
          className={`p-2 rounded-lg ${view === 'list' ? 'bg-primary-shade1 dark:bg-slate-700 text-primary-shade4' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}
        >
          <FiList className="w-6 h-6" />
        </button>
      </div>

      {/* Results Sections */}
      <section className="space-y-12">
        <div className="space-y-6">
          <h2 className="text-3xl font-semibold border-l-4 border-primary-shade4 pl-4 dark:text-white">
            Books
          </h2>
          {books.length > 0 ? (
            <BookGrid books={books} view={view} loading={loading} />
          ) : (
            !loading && <p className="text-gray-500 dark:text-slate-400">No books found</p>
          )}
        </div>

        <div className="space-y-6">
          <h2 className="text-3xl font-semibold border-l-4 border-primary-shade4 pl-4 dark:text-white">
            Authors
          </h2>
          {authors.length > 0 ? (
            <AuthorGrid authors={authors} view={view} loading={loading} />
          ) : (
            !loading && <p className="text-gray-500 dark:text-slate-400">No authors found</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default SearchPage;